import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { SettingsProvider } from '@/contexts/SettingsContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Toaster } from '@/components/ui/toaster';
import Layout from '@/components/Layout';
import Login from '@/pages/Login';
import AcceptInvite from '@/pages/AcceptInvite';
import AuthCallback from '@/pages/AuthCallback';

// Lazy-loaded pages for code splitting
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const BatchDetail = lazy(() => import('@/pages/BatchDetail'));
const DeviceDetail = lazy(() => import('@/pages/DeviceDetail'));
const Repairs = lazy(() => import('@/pages/Repairs'));
const RepairDetail = lazy(() => import('@/pages/RepairDetail'));
const Invoices = lazy(() => import('@/pages/Invoices'));
const Customers = lazy(() => import('@/pages/Customers'));
// Reports is now merged into Batch Center (PlanningHub.js) - see /sourcing?tab=analytics
// Forecasting is also merged into Batch Center (PlanningHub.js)
const Settings = lazy(() => import('@/pages/Settings'));
const SourcingHub = lazy(() => import('@/pages/PlanningHub'));
const Stock = lazy(() => import('@/pages/Stock'));
const CustomerDetail = lazy(() => import('@/pages/CustomerDetail'));
const BatchFunding = lazy(() => import('@/pages/BatchFunding'));
const Competitors = lazy(() => import('@/pages/Competitors'));
const CompetitorDetail = lazy(() => import('@/pages/CompetitorDetail'));
const Quotes = lazy(() => import('@/pages/Quotes'));
const Sales = lazy(() => import('@/pages/Sales'));
const DealDetail = lazy(() => import('@/pages/DealDetail'));
const Logistics = lazy(() => import('@/pages/Logistics'));
const Support = lazy(() => import('@/pages/Support'));
const Devices = lazy(() => import('@/pages/Devices'));
const ICASALicensing = lazy(() => import('@/pages/ICASALicensing'));
const Pricelist = lazy(() => import('@/pages/Pricelist'));
const PublicPricelist = lazy(() => import('@/pages/PublicPricelist'));
const Parts = lazy(() => import('@/pages/Parts'));
const PreOrders = lazy(() => import('@/pages/PreOrders'));
const XeroCallback = lazy(() => import('@/pages/XeroCallback'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
    </div>
  );
}

function ProtectedRoute({ children }: any) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#F2F4F6' }}>
      <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#1B7F79' }} />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return (
    <Layout>
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          {children}
        </Suspense>
      </ErrorBoundary>
    </Layout>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // Check URL fragment for session_id from OAuth callback BEFORE rendering routes
  // This prevents race conditions - detect during render, not in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  if (loading) return null;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/sourcing" element={<ProtectedRoute><SourcingHub /></ProtectedRoute>} />
      <Route path="/batches/:batchId" element={<ProtectedRoute><BatchDetail /></ProtectedRoute>} />
      <Route path="/batches/:batchId/funding" element={<ProtectedRoute><BatchFunding /></ProtectedRoute>} />
      <Route path="/devices/:deviceId" element={<ProtectedRoute><DeviceDetail /></ProtectedRoute>} />
      <Route path="/devices" element={<ProtectedRoute><Devices /></ProtectedRoute>} />
      <Route path="/repairs" element={<ProtectedRoute><Repairs /></ProtectedRoute>} />
      <Route path="/repairs/:repairId" element={<ProtectedRoute><RepairDetail /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
      <Route path="/customers/:customerId" element={<ProtectedRoute><CustomerDetail /></ProtectedRoute>} />
      <Route path="/reports" element={<Navigate to="/sourcing?tab=analytics" replace />} />
      <Route path="/forecasting" element={<Navigate to="/sourcing?tab=growth" replace />} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/dealnord" element={<Navigate to="/sourcing" replace />} />
      <Route path="/documents" element={<Navigate to="/reports?tab=documents" replace />} />
      <Route path="/planning" element={<Navigate to="/sourcing" replace />} />
      <Route path="/batches" element={<Navigate to="/sourcing" replace />} />
      <Route path="/stock" element={<ProtectedRoute><Stock /></ProtectedRoute>} />
      <Route path="/quotes" element={<ProtectedRoute><Quotes /></ProtectedRoute>} />
      <Route path="/sales" element={<ProtectedRoute><Sales /></ProtectedRoute>} />
      <Route path="/sales/:dealId" element={<ProtectedRoute><DealDetail /></ProtectedRoute>} />
      <Route path="/deals/:dealId" element={<ProtectedRoute><DealDetail /></ProtectedRoute>} />
      <Route path="/logistics" element={<ProtectedRoute><Logistics /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
      <Route path="/market-intel" element={<Navigate to="/competitors" replace />} />
      <Route path="/competitors" element={<ProtectedRoute><Competitors /></ProtectedRoute>} />
      <Route path="/competitors/:competitorId" element={<ProtectedRoute><CompetitorDetail /></ProtectedRoute>} />
      <Route path="/icasa" element={<ProtectedRoute><ICASALicensing /></ProtectedRoute>} />
      <Route path="/pricelist" element={<ProtectedRoute><Pricelist /></ProtectedRoute>} />
      <Route path="/pricelist/master" element={<Suspense fallback={<PageLoader />}><PublicPricelist /></Suspense>} />
      <Route path="/price-list" element={<Suspense fallback={<PageLoader />}><PublicPricelist /></Suspense>} />
      <Route path="/pricelist/:token" element={<Suspense fallback={<PageLoader />}><PublicPricelist /></Suspense>} />
      <Route path="/parts" element={<ProtectedRoute><Parts /></ProtectedRoute>} />
      <Route path="/preorders" element={<ProtectedRoute><PreOrders /></ProtectedRoute>} />
      <Route path="/xero/callback" element={<Suspense fallback={<PageLoader />}><XeroCallback /></Suspense>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <SettingsProvider>
              <AppRoutes />
              <Toaster />
            </SettingsProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

// Single shared React Query client. Sensible defaults for the ERP:
//  - 30s stale time (most ops don't change second-to-second)
//  - No auto-retry on 4xx (usually a bug, not transient)
//  - Refetch on window focus turned off (avoids noisy re-renders when
//    the user tabs between the ERP and their inbox)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        const status = error?.response?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

export default App;
